import { useEffect, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import {
  RefreshCw, Wifi, WifiOff, Trash2, Download, CheckCircle2,
  AlertCircle, Info, Smartphone,
} from 'lucide-react';

import { BytebeatLayout } from '@/components/BytebeatLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useServiceWorker } from '@/hooks/useServiceWorker';

type ActionStatus = 'idle' | 'busy' | 'done' | 'error';

export default function SettingsPage() {
  const { supported, registered, updateReady, checkForUpdate, applyUpdate, forceUpdate } =
    useServiceWorker();

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cacheSize, setCacheSize] = useState<string | null>(null);

  // Update status feedback
  const [checkStatus, setCheckStatus] = useState<ActionStatus>('idle');
  const [checkMsg, setCheckMsg] = useState('');
  const [forceStatus, setForceStatus] = useState<ActionStatus>('idle');
  const [forceMsg, setForceMsg] = useState('');
  const [clearStatus, setClearStatus] = useState<ActionStatus>('idle');
  const [clearMsg, setClearMsg] = useState('');

  // Online / offline
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Storage estimate
  useEffect(() => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(({ usage, quota }) => {
        if (usage !== undefined && quota !== undefined) {
          setCacheSize(`${(usage / 1024 / 1024).toFixed(2)} MB / ${(quota / 1024 / 1024).toFixed(0)} MB`);
        }
      }).catch(() => {});
    }
  }, []);

  const handleCheck = async () => {
    setCheckStatus('busy');
    setCheckMsg('');
    await checkForUpdate();
    if (updateReady) {
      setCheckStatus('done');
      setCheckMsg('An update is ready — click "Apply Update" to reload with it.');
    } else {
      setCheckStatus('idle');
      setCheckMsg('Already on the latest version.');
    }
  };

  const handleForceUpdate = async () => {
    setForceStatus('busy');
    setForceMsg('Unregistering service worker and clearing caches…');
    try {
      await forceUpdate();
      // forceUpdate() reloads, so we only reach here on error
    } catch (e) {
      setForceStatus('error');
      setForceMsg(String(e));
    }
  };

  const handleClearCache = async () => {
    setClearStatus('busy');
    setClearMsg('');
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      setClearStatus('done');
      setClearMsg('Cache cleared. Reload the page to re-fetch assets.');
    } catch (e) {
      setClearStatus('error');
      setClearMsg(String(e));
    }
  };

  useSeoMeta({
    title: 'Settings — Bytebeat Player',
    description: 'PWA and offline settings for the Bytebeat Player.',
  });

  return (
    <BytebeatLayout>
      <div className="max-w-xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary glow-cyan">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            PWA and offline settings for Bytebeat Player.
          </p>
        </div>

        {/* ── Status ── */}
        <Card className="border-border/40 bg-card/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-primary" />
              App Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Network */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                {isOnline
                  ? <Wifi className="w-4 h-4 text-green-400" />
                  : <WifiOff className="w-4 h-4 text-amber-400" />}
                <span>Network</span>
              </div>
              <Badge variant="outline" className={isOnline
                ? 'border-green-500/30 text-green-400'
                : 'border-amber-500/30 text-amber-400'}>
                {isOnline ? 'Online' : 'Offline'}
              </Badge>
            </div>

            <Separator className="bg-border/30" />

            {/* SW / PWA */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Download className="w-4 h-4 text-primary" />
                <span>Service Worker</span>
              </div>
              <Badge variant="outline" className={
                !supported ? 'border-destructive/30 text-destructive' :
                registered  ? 'border-green-500/30 text-green-400' :
                              'border-muted-foreground/30 text-muted-foreground'
              }>
                {!supported ? 'Unsupported' : registered ? 'Registered' : 'Not registered'}
              </Badge>
            </div>

            {registered && (
              <>
                <Separator className="bg-border/30" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Pending update</span>
                  <span className={updateReady ? 'text-amber-400' : ''}>
                    {updateReady ? '⚠ Waiting to activate' : '— none —'}
                  </span>
                </div>
              </>
            )}

            {cacheSize && (
              <>
                <Separator className="bg-border/30" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Storage used</span>
                  <span className="font-mono">{cacheSize}</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* ── Soft update ── */}
        <Card className="border-border/40 bg-card/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-accent" />
              Check &amp; Apply Update
            </CardTitle>
            <CardDescription className="text-xs">
              Ask the browser to check for a new service worker version. If one is waiting,
              "Apply Update" will activate it and reload the page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <StatusMessage status={checkStatus} message={checkMsg} />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 gap-2 border-border/50 hover:border-primary/40 text-sm"
                onClick={handleCheck}
                disabled={!supported || checkStatus === 'busy'}
              >
                <RefreshCw className={`w-4 h-4 ${checkStatus === 'busy' ? 'animate-spin' : ''}`} />
                Check for Updates
              </Button>
              <Button
                className="flex-1 gap-2 bg-primary/15 hover:bg-primary/25 border border-primary/40 text-primary text-sm"
                onClick={applyUpdate}
                disabled={!updateReady}
              >
                <CheckCircle2 className="w-4 h-4" />
                Apply Update
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ── Force update (nuke) ── */}
        <Card className="border-border/40 bg-card/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-accent" />
              Update PWA
            </CardTitle>
            <CardDescription className="text-xs">
              Unregisters the service worker, deletes all caches, then reloads — forcing
              the browser to download a completely fresh copy of the app. Use this if the
              app is stuck on an old version.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <StatusMessage status={forceStatus} message={forceMsg} />
            <Button
              className="w-full gap-2 bg-accent/20 hover:bg-accent/30 border border-accent/40 text-accent hover:text-accent text-sm"
              onClick={handleForceUpdate}
              disabled={forceStatus === 'busy'}
            >
              <RefreshCw className={`w-4 h-4 ${forceStatus === 'busy' ? 'animate-spin' : ''}`} />
              {forceStatus === 'busy' ? 'Updating…' : 'Update PWA'}
            </Button>
          </CardContent>
        </Card>

        {/* ── Cache management ── */}
        <Card className="border-border/40 bg-card/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-destructive/70" />
              Clear Cache
            </CardTitle>
            <CardDescription className="text-xs">
              Deletes all cached assets without touching the service worker registration.
              You will need to be online to re-fetch assets after clearing.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <StatusMessage status={clearStatus} message={clearMsg} />
            <Button
              variant="outline"
              className="w-full gap-2 border-destructive/30 hover:border-destructive/60 text-destructive/80 hover:text-destructive text-sm"
              onClick={handleClearCache}
              disabled={clearStatus === 'busy'}
            >
              <Trash2 className="w-4 h-4" />
              Clear Cache
            </Button>
          </CardContent>
        </Card>

        {/* ── Info ── */}
        <Card className="border-border/40 bg-card/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="w-4 h-4 text-muted-foreground" />
              About Offline Mode
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs text-muted-foreground">
            <p>
              Bytebeat Player is a Progressive Web App. Once the service worker is
              installed and assets are cached, it works entirely offline — no server needed.
            </p>
            <p>
              All bytebeat data lives in the URL fragment (after{' '}
              <code className="font-mono text-accent/60">#</code>).
              Links are fully self-contained and never require a database or backend.
            </p>
            <p>
              To install: open this page in Chrome, Edge, or Safari and tap the
              "Add to Home Screen" or install icon in the address bar.
            </p>
          </CardContent>
        </Card>
      </div>
    </BytebeatLayout>
  );
}

// ── Small helper ─────────────────────────────────────────────────────────────
function StatusMessage({ status, message }: { status: ActionStatus; message: string }) {
  if (!message) return null;
  const styles: Record<ActionStatus, string> = {
    idle:  'bg-muted/30 border-border/30 text-muted-foreground',
    busy:  'bg-muted/30 border-border/30 text-muted-foreground',
    done:  'bg-green-500/5 border-green-500/30 text-green-400',
    error: 'bg-destructive/5 border-destructive/30 text-destructive',
  };
  const icons: Record<ActionStatus, React.ReactNode> = {
    idle:  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />,
    busy:  <RefreshCw className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 animate-spin" />,
    done:  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />,
    error: <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />,
  };
  return (
    <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs border ${styles[status]}`}>
      {icons[status]}
      <span>{message}</span>
    </div>
  );
}
